/**
 * Reducer for Profile Information state management
 */

import { UserDetails } from "../../../api/types/user.types";
import {
  AvatarMetadata,
  AvatarUploadData,
} from "../../../api/types/avatar.types";
import * as types from "../../actionTypes/profileInformation";

export interface ProfileInformationState {
  loading: boolean;
  updating: boolean;
  error: string | null;
  updateError: string | null;
  userDetails: UserDetails | null;
  success: boolean;
  avatarLoading: boolean;
  avatarUploading: boolean;
  avatarError: string | null;
  avatarUploadError: string | null;
  avatarMetadata: AvatarMetadata | null;
}

const initialState: ProfileInformationState = {
  loading: false,
  updating: false,
  error: null,
  updateError: null,
  userDetails: null,
  success: false,
  avatarLoading: false,
  avatarUploading: false,
  avatarError: null,
  avatarUploadError: null,
  avatarMetadata: null,
};

/**
 * Action types for the reducer
 */
type ProfileInformationAction =
  | {
      type: typeof types.FETCH_USER_PROFILE_REQUEST;
      payload: { userId: string | number };
    }
  | { type: typeof types.FETCH_USER_PROFILE_SUCCESS; payload: UserDetails }
  | { type: typeof types.FETCH_USER_PROFILE_FAILURE; payload: string }
  | {
      type: typeof types.UPDATE_USER_PROFILE_REQUEST;
      payload: Partial<UserDetails>;
    }
  | { type: typeof types.UPDATE_USER_PROFILE_SUCCESS; payload: UserDetails }
  | { type: typeof types.UPDATE_USER_PROFILE_FAILURE; payload: string }
  | { type: typeof types.RESET_PROFILE_STATE }
  | {
      type: typeof types.FETCH_AVATAR_REQUEST;
      payload: { userId: string | number };
    }
  | { type: typeof types.FETCH_AVATAR_SUCCESS; payload: AvatarMetadata }
  | { type: typeof types.FETCH_AVATAR_FAILURE; payload: string }
  | { type: typeof types.UPLOAD_AVATAR_REQUEST }
  | { type: typeof types.UPLOAD_AVATAR_SUCCESS; payload: AvatarUploadData }
  | { type: typeof types.UPLOAD_AVATAR_FAILURE; payload: string };

/**
 * Profile Information Reducer
 */
const profileInformationReducer = (
  state: ProfileInformationState = initialState,
  action: ProfileInformationAction
): ProfileInformationState => {
  switch (action.type) {
    case types.FETCH_USER_PROFILE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: false,
      };

    case types.FETCH_USER_PROFILE_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        userDetails: action.payload,
        success: true,
      };

    case types.FETCH_USER_PROFILE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        userDetails: null,
        success: false,
      };

    case types.UPDATE_USER_PROFILE_REQUEST:
      return {
        ...state,
        updating: true,
        updateError: null,
      };

    case types.UPDATE_USER_PROFILE_SUCCESS:
      return {
        ...state,
        updating: false,
        updateError: null,
        userDetails: action.payload,
      };

    case types.UPDATE_USER_PROFILE_FAILURE:
      return {
        ...state,
        updating: false,
        updateError: action.payload,
      };

    case types.FETCH_AVATAR_REQUEST:
      return {
        ...state,
        avatarLoading: true,
        avatarError: null,
      };

    case types.FETCH_AVATAR_SUCCESS:
      return {
        ...state,
        avatarLoading: false,
        avatarError: null,
        avatarMetadata: action.payload,
      };

    case types.FETCH_AVATAR_FAILURE:
      return {
        ...state,
        avatarLoading: false,
        avatarError: action.payload,
        avatarMetadata: null,
      };

    case types.UPLOAD_AVATAR_REQUEST:
      return {
        ...state,
        avatarUploading: true,
        avatarUploadError: null,
      };

    case types.UPLOAD_AVATAR_SUCCESS: {
      const previousMetadata = state.avatarMetadata;
      const mergedMetadata: AvatarMetadata = {
        userId: action.payload.userId,
        avatarUrl: action.payload.avatarUrl,
        fileName: action.payload.fileName,
        uploadStatus: action.payload.uploadStatus,
        uploadedOn: previousMetadata?.uploadedOn ?? "",
        fileSize: previousMetadata?.fileSize ?? 0,
        mimeType: previousMetadata?.mimeType ?? "",
      };

      return {
        ...state,
        avatarUploading: false,
        avatarUploadError: null,
        avatarMetadata: mergedMetadata,
      };
    }

    case types.UPLOAD_AVATAR_FAILURE:
      return {
        ...state,
        avatarUploading: false,
        avatarUploadError: action.payload,
      };

    case types.RESET_PROFILE_STATE:
      return initialState;

    default:
      return state;
  }
};

export default profileInformationReducer;
